import axios from 'axios';
import ContextMenu from "@/components/molecules/ContextMenu/Index";
import Container from "@/components/atoms/Container";
import StandardBreadcrumb from "@/components/molecules/StandardBreadcrumb";
import { Tabs, Box } from "@radix-ui/themes";
import useMessageProps from "@/hooks/useMessageProps";
import useAccess from "@/hooks/useAccess";
import { Button, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import Ticket from "@/types/Ticket";
import History from "@/types/History";
import TicketForm, {
  TicketProps,
  useTicketForm,
} from "@/components/organisms/Ticket/Form";
import Select from "@/components/molecules/Select";
import { toast } from "react-toastify";
import { Eye, EyeOff, PenSquare, Copy, X, Plus } from "lucide-react";
import Detail from "@/components/organisms/Ticket/Detail";
import NoteTab from "@/components/organisms/Ticket/Tabs/NoteTab";
import QualityTab from "@/components/organisms/Ticket/Tabs/QualityTab";
import EstimationTab from "@/components/organisms/Ticket/Tabs/EstimationTab";
import HistoryTab from "@/components/organisms/Ticket/Tabs/HistoryTab";
import AddLogTickets from '@/components/organisms/Ticket/AddLogs';
import { router } from "@inertiajs/react";
import LogCategory from "@/types/LogCategory";
import Reply from '@/types/Reply';
import Note from "@/types/Note";

export type TicketPageProps = TicketProps & {
  ticket: Ticket;
  redirect_props: any;
  histories: History[];
};

const ShowTicket = (
  {
    ticket,
    redirect_props,
    histories,
    ...rest
  }: TicketPageProps) => {
  const [selectedTicketStatus, setSelectedTicketStatus] = useState<string>(ticket.status.id.toString());
  const [toastMessage, setToastMessage] = useState<{ success: boolean, message: string } | null>(null);
  const [isWatching, setIsWatching] = useState(ticket.is_watching);
  const { currentUser, getAccess } = useAccess();
  const form = useTicketForm(ticket);
  const [loading, setLoading] = useState(false);
  const [isInfoTab, setIsInfoTab] = useState(true);
  const [qualityTickets, setQualityTickets] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [totalLoggedHours, setTotalLoggedHours] = useState<string>("00:00");
  const [logCategories, setLogCategories] = useState<LogCategory[]>([]);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [ticketNotes, setTicketNotes] = useState<Note[]>(ticket.notes);
  const [edit, setEdit] = useState(false);
  const tabRef = useRef(null);
  const [active, setActive] = useState("info");
  const [restPropsData, setRestPropsData] = useState(rest);
  const [requiredDataLoading, setRequiredDataLoading] = useState(true);

  const breadcrumbItems = [
    {
      label: "Tickets",
      onClick: () => router.get("/tickets"),
    },
    {
      label: ticket.display_id,
    },
  ];

  useMessageProps({ redirect_props });

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  const handleTicketStatusChange = async (value: string) => {
    if (value === "6") {
      const subCount = ticket.internal_ticket_types.reduce((sum, item) => sum + item.internal_sub_ticket_type_count, 0);
      const isConfirmed = window.confirm(`Do you confirm that you delivered ${subCount} amount of sub ticket type count in this request? If not, please edit the count and then close the ticket.`);
      
      if (!isConfirmed) {
        return;
      }
    }

    setSelectedTicketStatus(value);

    try {
      const csrfToken = getCsrfToken();
      const response = await axios.put(`/tickets/${ticket.id}/set_status`, {
        ticket: {
          ticket_status_id: value,
        }
      }, {
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });

      if (response.data.success) {
        setToastMessage({ success: true, message: 'Ticket Status updated successfully' });
      } else {
        setSelectedTicketStatus(ticket.status.id.toString());
        setToastMessage({ success: false, message: response.data.message });
      }

    } catch (error) {
      console.log(error);
      setSelectedTicketStatus(ticket.status.id.toString());
      if ((error as any).response) {
        setToastMessage({ success: false, message: (error as any).response.data.errors.join(', ') || 'You do not have permission to perform this action.' });
      } else {
        setToastMessage({ success: false, message: 'An unexpected error occurred. Please try again.' });
      }
    }
  };

  const handleWatchTicket = async () => {
    try {
      if (!currentUser) {
        setToastMessage({ success: false, message: 'Current user is not available.' });
        return;
      }

      const response = await axios.get(`/admin/users/${currentUser.id}/watched_tickets/${ticket.id}`);
      setToastMessage({ success: true, message: response.data.message });
      setIsWatching((prev) => !prev);
    } catch (error) {
      if ((error as any).response) {
        setToastMessage({ success: false, message: (error as any).response.data.errors.join(', ') || 'You do not have permission to perform this action.' });
      } else {
        setToastMessage({ success: false, message: 'An unexpected error occurred. Please try again.' });
      }
    }
  }

  const handleCopyTicket = async () => {
    const isConfirmed = window.confirm("Are you sure you want to copy this Ticket?");

    if (!isConfirmed) {
      return;
    }

    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(`/tickets/${ticket.id}/copy`, {}, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const data = response.data;

      if (data.success === false) {
        setToastMessage({
          success: false,
          message: data.message || 'An error occurred while copying the ticket.',
        });

      } else {
        setToastMessage({
          success: true,
          message: 'Ticket was successfully copied. Redirecting to the new ticket...',
        });

        setTimeout(() => {
          window.location.href = `/tickets/${data.ticket.id}`;
        }, 2000);
      }
    } catch (error) {
      setToastMessage({
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      });
    }
  };

  useEffect(() => {
    if (toastMessage) {
      if (toastMessage.success) {
        toast.success(toastMessage.message);
      } else {
        toast.error(toastMessage.message);
      }
    }
  }, [toastMessage]);

  const onSubmit = (data: any) => {
    router.put(
      `/tickets/${ticket.id}`,
      data,
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  useEffect(() => {
    if (!loading && redirect_props?.success) {
      setEdit(false);
    }
  }, [loading]);

  useEffect(() => {
    if (tabRef?.current)
      [...(tabRef?.current as any).children]
        .find((el: any) => el.dataset.state === "active")
        .scrollIntoView({
          inline: "center",
        });
  }, [active]);

  useEffect(() => {
    let activeRequests = 0;

    const incrementRequest = () => {
      activeRequests += 1;
      setRequiredDataLoading(true);
    };

    const decrementRequest = () => {
      activeRequests -= 1;
      if (activeRequests === 0) {
        setRequiredDataLoading(false);
      }
    };

    setTimeout(() => {
      incrementRequest();
      axios
        .get(`/accounts/${ticket.account.id}/read_account_for_ticket`)
        .then(response => {
          setRestPropsData({
            ...restPropsData,
            internalOwners: response.data.users.filter(
              (user: any) =>
                user.status === "Active" &&
                !user.roles.some((role: any) => role.name === "client")
            ),
            sows: [],
            priorities: response.data.priorities,
            requesters: response.data.users.filter(
              (user: any) =>
                user.status === "Active" &&
                user.roles.some((role: any) => role.name === "client")
            ),
            ticket_types: response.data.ticket_types,
            internal_ticket_types: response.data.internal_ticket_types,
            countries: response.data.countries,
            functions: response.data.functions,
            departments: response.data.departments
          });
          
          setUsers(response.data.users.filter(
            (user: any) =>
              user.status === "Active" &&
              !user.roles.some((role: any) => role.name === "client")
          ));
        })
        .catch(error => console.error('Error fetching read_account_for_ticket:', error))
        .finally(decrementRequest);
    }, 20);

    setTimeout(() => {
      incrementRequest();
      axios
        .get('/log_categories/listing')
        .then(response => {
          if (ticket.display_id === 'Non-Chargeable') {
            setLogCategories(response.data.filter((category: any) => category.kind === "Non-Chargeable"));
          } else {
            setLogCategories(response.data.filter((category: any) => category.kind === "Chargeable"));
          }
        })
        .catch(error => console.error('Error fetching log categories:', error))
        .finally(decrementRequest);
    }, 20);

    setTimeout(() => {
      incrementRequest();
      axios
        .get(`/tickets/${ticket.id}/ticket_qas`)
        .then(response => {
          setQualityTickets(response.data.ticket_qas);
          decrementRequest();
        })
        .catch(error => console.error('Error fetching qa tickets:', error))
        .finally(decrementRequest);
    }, 20);

    setTimeout(() => {
      incrementRequest();
      axios
        .get(`/tickets/${ticket.id}/total_logged_hours`)
        .then(response => {
          setTotalLoggedHours(response.data.total_logged_hours);
        })
        .catch(error => console.error('Error fetching total logged hours:', error))
        .finally(decrementRequest);
    }, 20);

    setTimeout(() => {
      incrementRequest();
      axios
        .get(`/tickets/${ticket.id}/replies`)
        .then(response => {
          setReplies(response.data.replies);
        })
        .catch(error => console.error('Error fetching total logged hours:', error))
        .finally(decrementRequest);
    }, 20);
  }, [ticket]);

  const AddLogs = ({ ticket, logCategories, users }: any) => {
    const isContentReady = ticket && logCategories.length > 0 && users.length > 0;

    return (
      <>
        {isContentReady && (
          <ContextMenu
            content={
              <div>
                <AddLogTickets ticket={ticket} logCategories={logCategories} users={users} />
              </div>
            }
            clickable={requiredDataLoading}
          >

            <Button
              disabled={requiredDataLoading}
            >
              <div className='flex gap-1 items-center'> <div><Plus size="16" /> </div> <div> Add log</div>  </div>
            </Button>

          </ContextMenu>
        )}
      </>
    )
  }

  return (
    <>
      <Container>
        <StandardBreadcrumb items={breadcrumbItems} />
        <div className="flex items-center justify-between mb-6 pt-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Text className="font-bold text-xl">{ticket.display_id}</Text>
              <Select
                className="h-8 w-auto"
                options={ticket.account.ticket_statuses.map((status) => ({
                  label: status.name,
                  value: status.id.toString(),
                }))}
                value={selectedTicketStatus}
                onChange={handleTicketStatusChange}
                disabled={!getAccess(["update_ticket"])}
              />
              

            </div>
            <p>{ticket?.subject}</p>
          </div>
          {isInfoTab && (
            <div className="flex">
              <div className="mr-2">
                {getAccess(["create_log"]) && (
                  <AddLogs ticket={ticket} logCategories={logCategories} users={users} />
                )}
              </div>
              <div className="mr-2">
                <Button
                  onClick={handleWatchTicket}
                  disabled={false}
                >
                  {!isWatching ? <Eye size={16} /> : <EyeOff size={16} />}
                </Button>
              </div>
              <div className="mr-2">
                <Button
                  onClick={handleCopyTicket}
                  disabled={!getAccess(['copy'])}
                >
                  <>
                    <Copy size={16} />
                    <span className="ml-2">Copy</span>
                  </>
                </Button>
              </div>
              <Button
                onClick={() => setEdit(!edit)}
                disabled={!getAccess(["update_ticket"]) || requiredDataLoading}
              >
                {edit ? (
                  <>
                    <X size={16} className="mr-2" /> <span>Cancel</span>
                  </>
                ) : (
                  <>
                    <PenSquare size={16} />
                    <span>Edit</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </Container>
      <Tabs.Root
        value={active}
        onValueChange={(tab) => {
          const isInfoTab = tab === "info";
          setIsInfoTab(isInfoTab);
          if (edit && isInfoTab) setEdit(false);
          setActive(tab);
        }}
        className="account-tabs"
      >
        <Tabs.List className={"px-2"} ref={tabRef}>
          <Tabs.Trigger value="info">Information</Tabs.Trigger>

          {getAccess(["create_note"]) && (
            <Tabs.Trigger value="notes">Notes ({ticketNotes.length})</Tabs.Trigger>
          )}

          {getAccess(["quality_actions"]) && (
            <Tabs.Trigger value="quality">Quality</Tabs.Trigger>
          )}

          {getAccess(["set_estimation"]) && (
            <Tabs.Trigger value="estimation">Estimation</Tabs.Trigger>
          )}

          {getAccess(["listing_ticket_history"]) && (
            <Tabs.Trigger value="histories">Activities</Tabs.Trigger>
          )}
        </Tabs.List>

        <Box>
          <Tabs.Content value="info">
            <Container className="py-4">
              {edit ? (
                <TicketForm
                  form={form}
                  edit={edit}
                  ticket={ticket}
                  onSubmit={onSubmit}
                  data={restPropsData}
                  loading={loading}
                  errors={redirect_props?.errors}
                />
              ) : (
                <Detail ticket={ticket} replies={replies} setSelectedTicketStatus={setSelectedTicketStatus} />
              )}

            </Container>
          </Tabs.Content>

          {getAccess(["create_note"]) && (
            <Tabs.Content value="notes" className='m-10'>
              <NoteTab ticket={ticket} setTicketNotes={setTicketNotes} ticketNotes={ticketNotes}/>
            </Tabs.Content>
          )}

          {getAccess(["quality_actions"]) && (
            <Tabs.Content value="quality">
              <QualityTab ticket={ticket} setSelectedTicketStatus={setSelectedTicketStatus} qualityTickets={qualityTickets} users={users} />
            </Tabs.Content>
          )}

          {getAccess(["set_estimation"]) && (
            <Tabs.Content value="estimation">
              <EstimationTab ticket={ticket} totalLoggedHours={totalLoggedHours} />
            </Tabs.Content>
          )}

          {getAccess(["listing_ticket_history"]) && (
            <Tabs.Content value="histories">
              <HistoryTab histories={histories}/>
            </Tabs.Content>
          )}

        </Box>
      </Tabs.Root>
    </>
  );
};

export default ShowTicket;
